import { createContext, Dispatch, RefObject, SetStateAction } from 'react';
import { IConfigApp, IConfig, IEnvironment } from '../interfaces/environment';
import { IUser, IUserFirebase } from '../interfaces/user';
import { IRole } from '../interfaces/role';
import { Language } from '../interfaces/language';
import { IDBPDatabase } from 'idb';
import { TOptions } from 'i18next';
import { TParameter, TReturn } from '../i18n/i18n.model';
import { ITenant } from '../interfaces/Tenant';
type StateSetter<T> = Dispatch<SetStateAction<T>>;

const configApp = {} as IConfigApp;
const environment = {} as IEnvironment;
const config = {} as IConfig;
const roles: IRole[] = [];
const functions: string[] = [];
const features: string[] = [];

interface IContextApp {
  configApp: IConfigApp;
  setUser: StateSetter<IUser | undefined>;
  user?: IUser;
  setLoading: StateSetter<boolean>;
  loading: boolean;
  setLoadingMessage: StateSetter<string>;
  loadingMessage: string;
  setShowLoading: StateSetter<boolean>;
  showLoading: boolean;
  environment: IEnvironment;
  setEnvironment: StateSetter<IEnvironment | undefined>;
  config: IConfig;
  setConfig: StateSetter<IConfig | undefined>;
  roles: IRole[];
  setRoles: StateSetter<IRole[]>;
  functions: string[];
  setFunctions: StateSetter<string[]>;
  features: string[];
  setFeatures: StateSetter<string[]>;
  dataLoaded: boolean;
  setDataLoaded: StateSetter<boolean>;
  checkAuth: (lastTenantId?: number) => Promise<boolean>;
  mainMenuCollapsed: boolean;
  setMainMenuCollapsed: StateSetter<boolean>;
  menuBreakpointCollapsed: boolean;
  setMenuBreakpointCollapsed: StateSetter<boolean>;
  sessionExpired: boolean;
  setSessionExpired: StateSetter<boolean>;
  languages: Language[];
  userDataFirebase?: IUserFirebase;
  setUserDataFirebase: StateSetter<IUserFirebase | undefined>;
  pictureProfileUrl: string;
  setPictureProfileUrl: StateSetter<string>;
  show2FAReminder: boolean;
  setShow2FAReminder: StateSetter<boolean>;
  indexedDatabaseRef: RefObject<IDBPDatabase<unknown> | null>;
  maintenanceMode: boolean;
  setMaintenanceMode: React.Dispatch<React.SetStateAction<boolean>>;
  isRequiredAcceptTemrsAndConditions: boolean;
  setIsRequiredAcceptTemrsAndConditions: StateSetter<boolean>;
  t: <S extends TParameter>(p: S, options?: TOptions) => TReturn<S>;
  termsAndConditionsVersionActive: string;
  setTermsAndConditionsVersionActive: StateSetter<string>;
  userHasAcceptedTermsAndConditions: boolean;
  setUserHasAcceptedTermsAndConditions: (accepted: boolean) => void;
  getTermsAndConditions: () => Promise<void>;
  fullNameMandatory: boolean | undefined;
  setFullNameMandatory: StateSetter<boolean | undefined>;
  tenantsAssociatedToUser: ITenant[];
  setTenantsAssociatedToUser: StateSetter<ITenant[]>;
  getTenantsAssociatedWithUser: () => Promise<ITenant[]>;
  selectedTenantId: number | undefined;
  setSelectedTenantId: StateSetter<number | undefined>;
  isAppMultitenant: boolean;
}

const context: IContextApp = {
  configApp,
  setUser: () => {},
  user: undefined,
  setLoading: () => {},
  loading: false,
  setLoadingMessage: () => {},
  loadingMessage: '',
  setShowLoading: () => {},
  showLoading: true,
  environment,
  setEnvironment: () => {},
  config,
  setConfig: () => {},
  setRoles: () => {},
  setFunctions: () => {},
  setFeatures: () => {},
  features,
  roles,
  functions,
  dataLoaded: false,
  setDataLoaded: () => {},
  checkAuth: () => Promise.resolve(false),
  mainMenuCollapsed: false,
  setMainMenuCollapsed: () => {},
  menuBreakpointCollapsed: false,
  setMenuBreakpointCollapsed: () => {},
  sessionExpired: false,
  setSessionExpired: () => {},
  languages: [],
  setUserDataFirebase: () => {},
  userDataFirebase: undefined,
  pictureProfileUrl: '',
  setPictureProfileUrl: () => {},
  show2FAReminder: false,
  setShow2FAReminder: () => {},
  indexedDatabaseRef: { current: null },
  maintenanceMode: false,
  setMaintenanceMode: () => {
    // Intentional
  },
  isRequiredAcceptTemrsAndConditions: false,
  setIsRequiredAcceptTemrsAndConditions: () => {},
  t: (() => '') as IContextApp['t'],
  termsAndConditionsVersionActive: '',
  setTermsAndConditionsVersionActive: () => {},
  userHasAcceptedTermsAndConditions: false,
  setUserHasAcceptedTermsAndConditions: () => {},
  getTermsAndConditions: () => Promise.resolve(),
  fullNameMandatory: false,
  setFullNameMandatory: () => {},
  tenantsAssociatedToUser: [],
  setTenantsAssociatedToUser: () => {},
  getTenantsAssociatedWithUser: () => Promise.resolve([]),
  selectedTenantId: 0,
  setSelectedTenantId: () => {},
  isAppMultitenant: false,
};

export const ContextApp = createContext(context);
